<template>
  <div class="user-center">
    <div class="container">
      <div class="page-header">
        <h1 class="page-title">{{ $t('userCenter.title') }}</h1>
      </div>

      <div class="user-center-content">
        <el-tabs v-model="activeTab" type="card" class="user-tabs">
          <el-tab-pane :label="$t('userCenter.personalInfo')" name="personalInfo">
            <div class="card user-info-card">
              <div class="card-body">
                <div class="user-avatar">
                  <img src="@/assets/images/default-avatar.svg" :alt="$t('userCenter.userAvatar')" />
                </div>
                <div class="user-details" v-if="userInfo">
                  <div class="user-detail-item">
                    <span class="label">{{ $t('userCenter.username') }}</span>
                    <span class="value">{{ userInfo.name }}</span>
                  </div>
                  <div class="user-detail-item">
                    <span class="label">{{ $t('userCenter.role') }}</span>
                    <span class="value">{{ userInfo.roleName }}</span>
                  </div>
                </div>
              </div>
            </div>
          </el-tab-pane>

          <el-tab-pane :label="$t('userCenter.securitySettings')" name="security">
            <div class="card two-factor-card">
              <div class="card-body two-factor-body">
                <div class="two-factor-heading">
                  <div>
                    <h3>{{ $t('userCenter.twoFactorTitle') }}</h3>
                    <p>{{ $t('userCenter.twoFactorDescription') }}</p>
                  </div>
                  <el-switch
                    :model-value="twoFactorEnabled"
                    :loading="twoFactorLoading"
                    :disabled="twoFactorLoading"
                    @change="handleTwoFactorToggle"
                  />
                </div>

                <div v-if="twoFactorSetup" class="two-factor-setup">
                  <img v-if="twoFactorQr" :src="twoFactorQr" :alt="$t('userCenter.twoFactorQrAlt')" />
                  <p>{{ $t('userCenter.twoFactorScanOrEnter') }}</p>
                  <code>{{ twoFactorSetup.secret }}</code>
                  <a :href="twoFactorSetup.otpauthUri">{{ $t('userCenter.twoFactorOpenApp') }}</a>
                  <label for="two-factor-confirm-code">{{ $t('userCenter.twoFactorCode') }}</label>
                  <input
                    id="two-factor-confirm-code"
                    v-model.trim="twoFactorCode"
                    class="form-control"
                    inputmode="numeric"
                    autocomplete="one-time-code"
                    maxlength="6"
                    :placeholder="$t('userCenter.twoFactorCodePlaceholder')"
                  />
                  <button class="btn btn-primary" :disabled="twoFactorLoading" @click="confirmTwoFactor">
                    {{ twoFactorLoading ? $t('userCenter.submitting') : $t('userCenter.twoFactorConfirm') }}
                  </button>
                  <button class="btn btn-outline" :disabled="twoFactorLoading" @click="cancelTwoFactorSetup">
                    {{ $t('common.cancel') }}
                  </button>
                </div>

                <div v-if="twoFactorDisablePrompt && !twoFactorSetup" class="two-factor-setup">
                  <label for="two-factor-disable-password">{{ $t('userCenter.password') }}</label>
                  <input
                    id="two-factor-disable-password"
                    v-model="twoFactorPassword"
                    class="form-control"
                    type="password"
                    :placeholder="$t('userCenter.passwordPlaceholder')"
                  />
                  <label for="two-factor-disable-code">{{ $t('userCenter.twoFactorCode') }}</label>
                  <input
                    id="two-factor-disable-code"
                    v-model.trim="twoFactorCode"
                    class="form-control"
                    inputmode="numeric"
                    autocomplete="one-time-code"
                    maxlength="6"
                    :placeholder="$t('userCenter.twoFactorCodePlaceholder')"
                  />
                  <button class="btn btn-primary" :disabled="twoFactorLoading" @click="confirmDisableTwoFactor">
                    {{ $t('userCenter.twoFactorDisableConfirm') }}
                  </button>
                  <button class="btn btn-outline" :disabled="twoFactorLoading" @click="cancelDisableTwoFactor">
                    {{ $t('common.cancel') }}
                  </button>
                </div>
              </div>
            </div>
            <div class="card password-card">
              <div class="card-body">
                <div class="password-section">
                  <div class="section-header" @click="togglePasswordForm">
                    <h3>{{ $t('userCenter.changePassword') }}</h3>
                    <span class="toggle-icon">{{ showPasswordForm ? '▲' : '▼' }}</span>
                  </div>

                  <div class="password-form" v-if="showPasswordForm">
                    <div class="form-group">
                      <label for="newPassword">{{ $t('userCenter.newPassword') }}</label>
                      <input
                        type="password"
                        id="newPassword"
                        v-model="passwordForm.newPwd"
                        class="form-control"
                        :placeholder="$t('userCenter.newPasswordPlaceholder')"
                      />
                      <div class="error-message" v-if="errors.newPwd">{{ errors.newPwd }}</div>
                    </div>

                    <div class="form-group">
                      <label for="confirmPassword">{{ $t('userCenter.confirmNewPassword') }}</label>
                      <input
                        type="password"
                        id="confirmPassword"
                        v-model="passwordForm.confirmPwd"
                        class="form-control"
                        :placeholder="$t('userCenter.confirmNewPasswordPlaceholder')"
                      />
                      <div class="error-message" v-if="errors.confirmPwd">{{ errors.confirmPwd }}</div>
                    </div>

                    <div class="form-actions">
                      <button
                        class="btn btn-primary"
                        @click="updatePassword"
                        :disabled="isSubmitting"
                      >
                        {{ isSubmitting ? $t('userCenter.submitting') : $t('userCenter.confirmChange') }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </el-tab-pane>

          <el-tab-pane :label="$t('userCenter.rechargeRecords')" name="rechargeRecords">
            <div class="card recharge-records-card">
              <div class="card-body">
                <RechargeRecords />
              </div>
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>
    </div>
  </div>
</template>

<script>
import qrcode from 'qrcode-generator'
import {
  beginTwoFactorSetup,
  confirmTwoFactorSetup,
  disableTwoFactor,
  getTwoFactorStatus,
  updatePassword
} from '@/api/user'
import { ElMessage } from 'element-plus'
import RechargeRecords from '@/components/RechargeRecords.vue'

export default {
  name: 'UserCenterView',
  components: {
    RechargeRecords
  },
  data() {
    return {
      activeTab: 'personalInfo',
      userInfo: null,
      showPasswordForm: false,
      isSubmitting: false,
      passwordForm: {
        newPwd: '',
        confirmPwd: ''
      },
      errors: {
        newPwd: '',
        confirmPwd: ''
      },
      twoFactorEnabled: false,
      twoFactorLoading: false,
      twoFactorSetup: null,
      twoFactorQr: '',
      twoFactorCode: '',
      twoFactorPassword: '',
      twoFactorDisablePrompt: false
    }
  },
  created() {
    this.loadUserInfo()
    this.loadTwoFactorStatus()
  },
  methods: {
    async loadTwoFactorStatus() {
      try {
        const response = await getTwoFactorStatus()
        this.twoFactorEnabled = response.data?.enabled === true
      } catch (error) {
        console.error(this.$t('userCenter.twoFactorStatusFailed'), error)
      }
    },
    async handleTwoFactorToggle(enabled) {
      if (enabled) {
        await this.startTwoFactorSetup()
        return
      }
      this.twoFactorDisablePrompt = true
      this.twoFactorCode = ''
      this.twoFactorPassword = ''
    },
    async startTwoFactorSetup() {
      this.twoFactorLoading = true
      try {
        const response = await beginTwoFactorSetup()
        this.twoFactorSetup = response.data
        const qr = qrcode(0, 'M')
        qr.addData(response.data.otpauthUri)
        qr.make()
        this.twoFactorQr = qr.createDataURL(5, 8)
      } catch (error) {
        console.error(this.$t('userCenter.twoFactorSetupFailed'), error)
        ElMessage.error(error.response?.data?.msg || error.message || this.$t('userCenter.twoFactorSetupFailed'))
      } finally {
        this.twoFactorLoading = false
      }
    },
    async confirmTwoFactor() {
      if (!/^\d{6}$/.test(this.twoFactorCode)) {
        ElMessage.error(this.$t('userCenter.twoFactorCodeInvalid'))
        return
      }
      this.twoFactorLoading = true
      try {
        await confirmTwoFactorSetup(this.twoFactorCode)
        this.twoFactorEnabled = true
        this.twoFactorSetup = null
        this.twoFactorQr = ''
        this.twoFactorCode = ''
        ElMessage.success(this.$t('userCenter.twoFactorEnabled'))
      } catch (error) {
        console.error(this.$t('userCenter.twoFactorSetupFailed'), error)
      } finally {
        this.twoFactorLoading = false
      }
    },
    cancelTwoFactorSetup() {
      this.twoFactorSetup = null
      this.twoFactorQr = ''
      this.twoFactorCode = ''
    },
    async confirmDisableTwoFactor() {
      if (!this.twoFactorPassword || !/^\d{6}$/.test(this.twoFactorCode)) {
        ElMessage.error(this.$t('userCenter.twoFactorDisableFieldsRequired'))
        return
      }
      this.twoFactorLoading = true
      try {
        await disableTwoFactor(this.twoFactorPassword, this.twoFactorCode)
        this.twoFactorEnabled = false
        this.twoFactorDisablePrompt = false
        this.twoFactorPassword = ''
        this.twoFactorCode = ''
        ElMessage.success(this.$t('userCenter.twoFactorDisabled'))
      } catch (error) {
        console.error(this.$t('userCenter.twoFactorDisableFailed'), error)
      } finally {
        this.twoFactorLoading = false
      }
    },
    cancelDisableTwoFactor() {
      this.twoFactorDisablePrompt = false
      this.twoFactorPassword = ''
      this.twoFactorCode = ''
    },
    loadUserInfo() {
      const token = localStorage.getItem('tianqi-token')
      if (!token) {
        this.$router.push('/login')
        return
      }

      const userInfoStr = localStorage.getItem('tianqi-user')
      if (userInfoStr) {
        try {
          this.userInfo = JSON.parse(userInfoStr)
        } catch (e) {
          console.error(this.$t('userCenter.messages.parseUserInfoFailed'), e)
        }
      }
    },
    togglePasswordForm() {
      this.showPasswordForm = !this.showPasswordForm
      if (!this.showPasswordForm) {
        this.resetPasswordForm()
      }
    },
    resetPasswordForm() {
      this.passwordForm = {
        newPwd: '',
        confirmPwd: ''
      }
      this.errors = {
        newPwd: '',
        confirmPwd: ''
      }
    },
    validatePasswordForm() {
      let isValid = true
      this.errors = {
        newPwd: '',
        confirmPwd: ''
      }

      if (!this.passwordForm.newPwd) {
        this.errors.newPwd = this.$t('userCenter.validation.newPasswordRequired')
        isValid = false
      } else if (this.passwordForm.newPwd.length < 6) {
        this.errors.newPwd = this.$t('userCenter.validation.newPasswordTooShort')
        isValid = false
      }

      if (!this.passwordForm.confirmPwd) {
        this.errors.confirmPwd = this.$t('userCenter.validation.confirmPasswordRequired')
        isValid = false
      } else if (this.passwordForm.confirmPwd !== this.passwordForm.newPwd) {
        this.errors.confirmPwd = this.$t('userCenter.validation.passwordMismatch')
        isValid = false
      }

      return isValid
    },
    async updatePassword() {
      if (!this.validatePasswordForm()) {
        return
      }

      this.isSubmitting = true

      try {
        const response = await updatePassword(this.passwordForm.newPwd)

        if (response.success) {
          ElMessage.success(this.$t('userCenter.messages.changePasswordSuccess'))
          this.togglePasswordForm()
        } else {
          ElMessage.error(this.$t('userCenter.messages.changePasswordFailed') + ': ' + (response.msg || this.$t('console.unknownError')))
        }
      } catch (error) {
        console.error(this.$t('userCenter.messages.changePasswordError'), error)
        ElMessage.error(this.$t('userCenter.messages.changePasswordError') + ': ' + (error.response?.data?.msg || error.message || this.$t('console.unknownError')))
      } finally {
        this.isSubmitting = false
      }
    }
  },
  metaInfo() {
    return {
      title: this.$t('titles.userCenter')
    }
  }
}
</script>

<style scoped>
.user-center {
  padding: 40px 0 80px;
}

.page-header {
  margin-bottom: 30px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
}

.user-center-content {
  width: 100%;
}

.user-tabs {
  width: 100%;
}

.user-tabs :deep(.el-tabs__content) {
  padding: 0;
}

.two-factor-body {
  display: grid;
  gap: 20px;
}

.two-factor-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.two-factor-heading h3 {
  margin: 0 0 6px;
  color: var(--text-primary);
}

.two-factor-heading p,
.two-factor-setup p {
  margin: 0;
  color: var(--text-secondary);
}

.two-factor-setup {
  display: grid;
  justify-items: start;
  gap: 12px;
  max-width: 420px;
}

.two-factor-setup img {
  width: 180px;
  height: 180px;
  border: 1px solid var(--border-color);
  padding: 8px;
  background: #fff;
}

.two-factor-setup code {
  padding: 8px 10px;
  background: var(--hover-color);
  overflow-wrap: anywhere;
  user-select: all;
}

.two-factor-setup .form-control {
  width: 100%;
}

@media (max-width: 768px) {
  .user-center {
    padding: 20px 0 40px;
  }

  .page-title {
    font-size: 24px;
  }

  .card {
    border-radius: var(--radius-md);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  .card-header {
    padding: 14px 20px;
  }

  .card-header h2 {
    font-size: 16px;
  }

  .card-body {
    padding: 20px;
  }

  .user-avatar img {
    width: 80px;
    height: 80px;
  }

  .user-detail-item .label {
    width: 70px;
    font-size: 14px;
  }

  .user-detail-item .value {
    font-size: 14px;
  }

  .section-header {
    padding: 14px;
  }

  .section-header h3 {
    font-size: 15px;
  }

  .password-form {
    padding: 20px 14px;
  }

  .form-control {
    padding: 12px;
    font-size: 16px;
    border-radius: var(--radius-md);
  }

  .btn {
    padding: 12px 24px;
    font-size: 16px;
    min-height: 44px;
    border-radius: var(--radius-md);
  }
}

@media (max-width: 480px) {
  .user-center {
    padding: 16px 0 30px;
  }

  .container {
    padding: 0 12px;
  }

  .page-header {
    margin-bottom: 24px;
  }

  .page-title {
    font-size: 20px;
  }

  .card {
    border-radius: var(--radius-sm);
    margin: 0 -4px;
  }

  .card-header {
    padding: 12px 16px;
  }

  .card-header h2 {
    font-size: 15px;
  }

  .card-body {
    padding: 16px;
  }

  .user-avatar {
    margin-bottom: 16px;
  }

  .user-avatar img {
    width: 70px;
    height: 70px;
  }

  .user-details {
    gap: 10px;
  }

  .user-detail-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 8px;
    background-color: rgba(15, 23, 42, 0.02);
    border-radius: var(--radius-sm);
  }

  .user-detail-item .label {
    width: auto;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .user-detail-item .value {
    font-size: 15px;
    font-weight: 500;
  }

  .section-header {
    padding: 12px;
  }

  .section-header h3 {
    font-size: 14px;
  }

  .password-form {
    padding: 16px 12px;
  }

  .form-group {
    margin-bottom: 16px;
  }

  .form-group label {
    margin-bottom: 6px;
    font-size: 13px;
    font-weight: 600;
  }

  .form-control {
    padding: 14px 12px;
    font-size: 16px;
    border-radius: var(--radius-md);
  }

  .error-message {
    font-size: 11px;
    margin-top: 3px;
  }

  .form-actions {
    margin-top: 20px;
  }

  .btn {
    padding: 14px 24px;
    font-size: 15px;
    font-weight: 600;
    min-height: 48px;
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 360px) {
  .container {
    padding: 0 8px;
  }

  .page-title {
    font-size: 18px;
  }

  .card {
    margin: 0 -2px;
  }

  .card-header {
    padding: 10px 12px;
  }

  .card-header h2 {
    font-size: 14px;
  }

  .card-body {
    padding: 12px;
  }

  .user-avatar img {
    width: 60px;
    height: 60px;
  }

  .user-detail-item {
    padding: 6px;
  }

  .user-detail-item .label {
    font-size: 11px;
  }

  .user-detail-item .value {
    font-size: 14px;
  }

  .section-header {
    padding: 10px;
  }

  .password-form {
    padding: 12px 10px;
  }

  .form-group {
    margin-bottom: 14px;
  }

  .form-group label {
    font-size: 12px;
  }

  .form-control {
    padding: 12px 10px;
    font-size: 16px;
  }

  .btn {
    padding: 12px 20px;
    font-size: 14px;
    min-height: 44px;
  }

  .toggle-icon {
    font-size: 14px;
  }
}

.card {
  background-color: #fff;
  border-radius: var(--radius-md);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin-top: 20px;
}

.card-header {
  padding: 16px 24px;
  border-bottom: 1px solid var(--border-color);
  background-color: rgba(15, 23, 42, 0.02);
}

.card-header h2 {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.card-body {
  padding: 24px;
}

.user-avatar {
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
}

.user-avatar img {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid var(--border-color);
}

.user-details {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.user-detail-item {
  display: flex;
  align-items: center;
}

.user-detail-item .label {
  width: 80px;
  font-weight: 500;
  color: var(--text-secondary);
}

.user-detail-item .value {
  color: var(--text-primary);
}

.password-section {
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  cursor: pointer;
  background-color: rgba(15, 23, 42, 0.02);
}

.section-header h3 {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: var(--text-primary);
}

.toggle-icon {
  color: var(--text-secondary);
}

.password-form {
  padding: 24px 16px;
  border-top: 1px solid var(--border-color);
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: var(--text-primary);
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-control:focus {
  border-color: var(--primary-color);
  outline: none;
}

.error-message {
  color: #dc2626;
  font-size: 12px;
  margin-top: 4px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
}

.btn {
  padding: 10px 20px;
  border-radius: var(--radius-sm);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-primary {
  background-color: var(--primary-color);
  color: #fff;
}

.btn-primary:hover {
  background-color: var(--primary-dark);
}

.btn-primary:disabled {
  background-color: #93c5fd;
  cursor: not-allowed;
}

.recharge-records-card {
  margin-top: 0;
}

.recharge-records-card .card-body {
  padding: 20px;
}
</style>
